import React, { useState, useEffect } from 'react';
import { priceUpdateService, CreatePriceUpdateData } from '../../services/priceUpdateService';
import { PriceUpdate } from '../../types';
import { FiDollarSign, FiPlus, FiEdit, FiTrash2, FiCalendar, FiX } from 'react-icons/fi';
import Icon from '../common/Icon';

interface PriceUpdateHistoryProps {
  entityType: 'customer' | 'lead' | 'tender';
  entityId: number;
  entityName: string;
}

const PriceUpdateHistory: React.FC<PriceUpdateHistoryProps> = ({ entityType, entityId, entityName }) => {
  const [priceUpdates, setPriceUpdates] = useState<PriceUpdate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPriceUpdate, setSelectedPriceUpdate] = useState<PriceUpdate | null>(null);
  const [formData, setFormData] = useState<CreatePriceUpdateData>({
    entity_type: entityType,
    entity_id: entityId,
    price: 0,
    update_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadPriceUpdates();
  }, [entityType, entityId]);

  const loadPriceUpdates = async () => {
    try {
      setLoading(true);
      const data = await priceUpdateService.getByEntity(entityType, entityId);
      setPriceUpdates(data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load price updates');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      await priceUpdateService.create(formData);
      setShowAddModal(false);
      setFormData({
        entity_type: entityType,
        entity_id: entityId,
        price: 0,
        update_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      loadPriceUpdates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add price update');
    }
  };

  const handleEdit = (priceUpdate: PriceUpdate) => {
    setSelectedPriceUpdate(priceUpdate);
    setFormData({
      entity_type: entityType,
      entity_id: entityId,
      price: priceUpdate.price,
      update_date: priceUpdate.update_date,
      notes: priceUpdate.notes || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPriceUpdate) return;
    try {
      setError('');
      await priceUpdateService.update(selectedPriceUpdate.id, {
        price: formData.price,
        update_date: formData.update_date,
        notes: formData.notes
      });
      setShowEditModal(false);
      setSelectedPriceUpdate(null);
      loadPriceUpdates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update price');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this price update?')) return;
    try {
      await priceUpdateService.delete(id);
      loadPriceUpdates();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete price update');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Icon component={FiDollarSign} className="w-5 h-5 text-green-600" />
          Price History
        </h3>
        <button
          onClick={() => setShowAddModal(true)}
          className="px-3 py-1.5 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium flex items-center gap-1 font-bold"
        >
          <Icon component={FiPlus} className="w-4 h-4" />
          <span className="hidden sm:inline">Add Price</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading price history...</div>
      ) : priceUpdates.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No price updates yet</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Updated By</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {priceUpdates.map((update) => (
                <tr key={update.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {new Date(update.update_date).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                    ₹{update.price.toLocaleString('en-IN')}
                  </td>
                  <td className="px-3 py-2 text-sm text-gray-500">
                    {update.notes || '-'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                    {update.updated_by_name || '-'}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(update)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                      title="Edit"
                    >
                      <Icon component={FiEdit} className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(update.id)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <Icon component={FiTrash2} className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
          <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base sm:text-lg font-bold text-gray-900">Add Price Update</h3>
              <button
                type="button"
                onClick={() => { setShowAddModal(false); setError(''); }}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close"
              >
                <Icon component={FiX} className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAdd} className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Price *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Update Date *</label>
                <input
                  type="date"
                  required
                  value={formData.update_date}
                  onChange={(e) => setFormData({ ...formData, update_date: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                >
                  Add Price Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPriceUpdate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-3 sm:p-4">
          <div className="relative top-2 sm:top-10 mx-auto p-4 sm:p-5 border w-full max-w-md shadow-xl rounded-lg bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-base sm:text-lg font-bold text-gray-900">Edit Price Update</h3>
              <button
                type="button"
                onClick={() => { setShowEditModal(false); setSelectedPriceUpdate(null); setError(''); }}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close"
              >
                <Icon component={FiX} className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleUpdate} className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Price *</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Update Date *</label>
                <input
                  type="date"
                  required
                  value={formData.update_date}
                  onChange={(e) => setFormData({ ...formData, update_date: e.target.value })}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedPriceUpdate(null);
                    setError('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-yellow-500 text-black-950 rounded-md hover:bg-yellow-600 text-sm font-medium font-bold"
                >
                  Update Price
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceUpdateHistory;




